import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Text from "../components/atoms/Text";
import Button from "../components/atoms/Button";
import Input from "../components/molecules/Input";
import products from "../data/products.json";
import { countryOptions, cityOptionsByCountry } from "../data/locationOptions";
import { useUser } from "../context/UserContext";
import { useNotification } from "../context/NotificationContext";

function formatCurrency(value) {
  return `$${Number(value).toFixed(2)}`;
}

function splitFullName(fullName = "") {
  const parts = fullName.trim().split(" ").filter(Boolean);

  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

function buildAddressFromUser(currentUser) {
  const billingAddress = currentUser?.billingAddress || {};
  const { firstName, lastName } = splitFullName(currentUser?.name || "");

  return {
    firstName: billingAddress.firstName || firstName,
    lastName: billingAddress.lastName || lastName,
    companyName: billingAddress.companyName || "",
    address: billingAddress.address || "",
    country: billingAddress.country || "",
    city: billingAddress.city || "",
    zipCode: billingAddress.zipCode || "",
    email: billingAddress.email || currentUser?.email || "",
    phone: billingAddress.phone || "",
  };
}

function renderSelect(value, onChange, placeholder, options) {
  return (
    <div style={{ position: "relative" }}>
      <select
        value={value}
        onChange={onChange}
        style={{
          width: "100%",
          height: "46px",
          border: "1px solid var(--gray1)",
          borderRadius: "6px",
          padding: "0 40px 0 16px",
          appearance: "none",
          outline: "none",
          color: value ? "var(--gray9)" : "var(--gray4)",
          fontFamily: "inherit",
          fontSize: "14px",
          backgroundColor: "var(--white)",
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span
        style={{
          position: "absolute",
          right: "16px",
          top: "50%",
          transform: "translateY(-50%)",
          color: "var(--gray5)",
          pointerEvents: "none",
          fontSize: "12px",
        }}
      >
        v
      </span>
    </div>
  );
}

function renderField(label, field) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <Text
        size="px12"
        weight="w400"
        color="gray9"
        extraStyles={{ display: "block", textAlign: "start" }}
      >
        {label}
      </Text>
      {field}
    </div>
  );
}

export default function Checkout() {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { currentUser, isAuthenticated, isLoadingUser, placeOrder } = useUser();
  const [billingForm, setBillingForm] = useState(() => buildAddressFromUser(currentUser));
  const [shipToDifferentAddress, setShipToDifferentAddress] = useState(false);
  const [shippingForm, setShippingForm] = useState(() => buildAddressFromUser(currentUser));
  const [orderNotes, setOrderNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash on Delivery");

  useEffect(() => {
    const nextAddress = buildAddressFromUser(currentUser);
    setBillingForm(nextAddress);
    setShippingForm(nextAddress);
  }, [currentUser]);

  const checkoutItems = useMemo(() => {
    if (!currentUser?.cart?.length) {
      return [];
    }

    return currentUser.cart
      .map((cartItem) => {
        const product = products.find((item) => item.id === cartItem.productId);

        if (!product) {
          return null;
        }

        return {
          ...product,
          quantity: cartItem.quantity,
          lineTotal: product.price * cartItem.quantity,
        };
      })
      .filter(Boolean);
  }, [currentUser]);

  const subtotal = useMemo(
    () => checkoutItems.reduce((sum, item) => sum + item.lineTotal, 0),
    [checkoutItems],
  );
  const totalQuantity = useMemo(
    () => checkoutItems.reduce((sum, item) => sum + item.quantity, 0),
    [checkoutItems],
  );
  const shipping = currentUser?.cartCoupon === "FREESHIP" ? 0 : totalQuantity * 2;
  const total = subtotal + shipping;

  const billingCityOptions = cityOptionsByCountry[billingForm.country] || [];
  const shippingCityOptions = cityOptionsByCountry[shippingForm.country] || [];

  function updateBillingField(field, value) {
    setBillingForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  }

  function updateShippingField(field, value) {
    setShippingForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  }

  function hasRequiredAddressFields(address) {
    return (
      address.firstName.trim() &&
      address.lastName.trim() &&
      address.address.trim() &&
      address.country.trim() &&
      address.city.trim() &&
      address.zipCode.trim() &&
      address.email.trim() &&
      address.phone.trim()
    );
  }

  async function handlePlaceOrder(event) {
    event.preventDefault();

    if (!checkoutItems.length) {
      notify("Your cart is empty.", "error");
      return;
    }

    const normalizedBilling = {
      ...billingForm,
      email: billingForm.email.trim().toLowerCase(),
    };
    const normalizedShipping = shipToDifferentAddress
      ? {
          ...shippingForm,
          email: shippingForm.email.trim().toLowerCase(),
        }
      : normalizedBilling;

    if (!hasRequiredAddressFields(normalizedBilling)) {
      notify("Please complete all required billing fields.", "error");
      return;
    }

    if (shipToDifferentAddress && !hasRequiredAddressFields(normalizedShipping)) {
      notify("Please complete all required shipping fields.", "error");
      return;
    }

    const result = await placeOrder({
      billingAddress: normalizedBilling,
      shippingAddress: normalizedShipping,
      orderNotes: orderNotes.trim(),
      paymentMethod,
      subtotal,
      shipping,
      total,
    });

    if (result?.success) {
      navigate("/profile");
    }
  }

  if (isLoadingUser) {
    return (
      <div style={{ padding: "60px 300px 100px", backgroundColor: "var(--white)" }}>
        <Text size="px32" weight="w600" color="gray9">
          Loading checkout...
        </Text>
      </div>
    );
  }

  if (isLoadingUser) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <div style={{ padding: "60px 300px 100px", backgroundColor: "var(--white)" }}>
        <Text size="px32" weight="w600" color="gray9">
          Please sign in to continue to checkout.
        </Text>
      </div>
    );
  }

  if (!checkoutItems.length) {
    return (
      <div style={{ padding: "60px 300px 100px", backgroundColor: "var(--white)" }}>
        <Text size="px32" weight="w600" color="gray9" extraStyles={{ marginBottom: "16px" }}>
          Your cart is empty.
        </Text>
        <Link to="/shop" style={{ textDecoration: "none" }}>
          <Button size="medium" variant="fill">
            Continue Shopping
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="checkout-page" style={{ padding: "40px 300px 100px", backgroundColor: "var(--white)" }}>
      <form
        className="checkout-layout"
        onSubmit={handlePlaceOrder}
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 360px",
          gap: "24px",
          alignItems: "start",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "32px", minWidth: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Text size="px24" weight="w500" color="gray9">
              Billing Information
            </Text>

            <div className="checkout-grid checkout-grid--three" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
              {renderField(
                "First name",
                <Input
                  placeholder="Your first name"
                  value={billingForm.firstName}
                  onChange={(event) => updateBillingField("firstName", event.target.value)}
                  width="100%"
                />,
              )}
              {renderField(
                "Last name",
                <Input
                  placeholder="Your last name"
                  value={billingForm.lastName}
                  onChange={(event) => updateBillingField("lastName", event.target.value)}
                  width="100%"
                />,
              )}
              {renderField(
                "Company Name (optional)",
                <Input
                  placeholder="Company name"
                  value={billingForm.companyName}
                  onChange={(event) => updateBillingField("companyName", event.target.value)}
                  width="100%"
                />,
              )}
            </div>

            {renderField(
              "Street Address",
              <Input
                placeholder="Street address"
                value={billingForm.address}
                onChange={(event) => updateBillingField("address", event.target.value)}
                width="100%"
              />,
            )}

            <div className="checkout-grid checkout-grid--three" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
              {renderField(
                "Country / Region",
                renderSelect(
                  billingForm.country,
                  (event) => {
                    updateBillingField("country", event.target.value);
                    updateBillingField("city", "");
                  },
                  "Select",
                  countryOptions,
                ),
              )}
              {renderField(
                "Cities",
                renderSelect(
                  billingForm.city,
                  (event) => updateBillingField("city", event.target.value),
                  "Select city",
                  billingCityOptions,
                ),
              )}
              {renderField(
                "Zip Code",
                <Input
                  placeholder="Zip Code"
                  value={billingForm.zipCode}
                  onChange={(event) => updateBillingField("zipCode", event.target.value)}
                  width="100%"
                />,
              )}
            </div>

            <div className="checkout-grid checkout-grid--two" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              {renderField(
                "Email",
                <Input
                  type="email"
                  placeholder="Email Address"
                  value={billingForm.email}
                  onChange={(event) => updateBillingField("email", event.target.value)}
                  width="100%"
                />,
              )}
              {renderField(
                "Phone",
                <Input
                  placeholder="Phone number"
                  value={billingForm.phone}
                  onChange={(event) => updateBillingField("phone", event.target.value)}
                  width="100%"
                />,
              )}
            </div>

            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={shipToDifferentAddress}
                onChange={(event) => setShipToDifferentAddress(event.target.checked)}
              />
              <Text size="px14" weight="w400" color="gray6">
                Ship to a different address
              </Text>
            </label>

            {shipToDifferentAddress ? (
              <div
                className="checkout-shipping-box"
                style={{
                  border: "1px solid var(--gray1)",
                  borderRadius: "8px",
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <Text size="px20" weight="w500" color="gray9">
                  Shipping Address
                </Text>

                <div className="checkout-grid checkout-grid--two" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  {renderField(
                    "First name",
                    <Input
                      placeholder="First name"
                      value={shippingForm.firstName}
                      onChange={(event) => updateShippingField("firstName", event.target.value)}
                      width="100%"
                    />,
                  )}
                  {renderField(
                    "Last name",
                    <Input
                      placeholder="Last name"
                      value={shippingForm.lastName}
                      onChange={(event) => updateShippingField("lastName", event.target.value)}
                      width="100%"
                    />,
                  )}
                </div>

                {renderField(
                  "Street Address",
                  <Input
                    placeholder="Street address"
                    value={shippingForm.address}
                    onChange={(event) => updateShippingField("address", event.target.value)}
                    width="100%"
                  />,
                )}

                <div className="checkout-grid checkout-grid--three" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
                  {renderField(
                    "Country / Region",
                    renderSelect(
                      shippingForm.country,
                      (event) => {
                        updateShippingField("country", event.target.value);
                        updateShippingField("city", "");
                      },
                      "Select",
                      countryOptions,
                    ),
                  )}
                  {renderField(
                    "Cities",
                    renderSelect(
                      shippingForm.city,
                      (event) => updateShippingField("city", event.target.value),
                      "Select city",
                      shippingCityOptions,
                    ),
                  )}
                  {renderField(
                    "Zip Code",
                    <Input
                      placeholder="Zip Code"
                      value={shippingForm.zipCode}
                      onChange={(event) => updateShippingField("zipCode", event.target.value)}
                      width="100%"
                    />,
                  )}
                </div>

                <div className="checkout-grid checkout-grid--two" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  {renderField(
                    "Email",
                    <Input
                      type="email"
                      placeholder="Email Address"
                      value={shippingForm.email}
                      onChange={(event) => updateShippingField("email", event.target.value)}
                      width="100%"
                    />,
                  )}
                  {renderField(
                    "Phone",
                    <Input
                      placeholder="Phone number"
                      value={shippingForm.phone}
                      onChange={(event) => updateShippingField("phone", event.target.value)}
                      width="100%"
                    />,
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Text
              size="px24"
              weight="w500"
              color="gray9"
              extraStyles={{ display: "block", textAlign: "start" }}
            >
              Additional Info
            </Text>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <Text
                size="px12"
                weight="w400"
                color="gray9"
                extraStyles={{ display: "block", textAlign: "start" }}
              >
                Order Notes (optional)
              </Text>
              <textarea
                value={orderNotes}
                onChange={(event) => setOrderNotes(event.target.value)}
                placeholder="Notes about your order, e.g. special notes for delivery"
                style={{
                  width: "100%",
                  height: "80px",
                  boxSizing: "border-box",
                  border: "1px solid var(--gray1)",
                  borderRadius: "6px",
                  padding: "14px 16px",
                  fontFamily: "inherit",
                  fontSize: "14px",
                  resize: "none",
                  outline: "none",
                }}
              />
            </div>
          </div>
        </div>

        <div
          className="checkout-summary"
          style={{
            border: "1px solid var(--gray1)",
            borderRadius: "8px",
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            backgroundColor: "var(--white)",
          }}
        >
          <Text size="px20" weight="w500" color="gray9" extraStyles={{ display: "block", textAlign: "start" }}>
            Order Summary
          </Text>

          {checkoutItems.map((item) => (
            <div
              key={item.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                <img
                  src={item.image}
                  alt={item.name}
                  style={{ width: "60px", height: "60px", objectFit: "contain", flexShrink: 0 }}
                />
                <Text size="px14" weight="w400" color="gray9" extraStyles={{ textAlign: "start" }}>
                  {item.name} x{item.quantity}
                </Text>
              </div>
              <Text size="px14" weight="w500" color="gray9">
                {formatCurrency(item.lineTotal)}
              </Text>
            </div>
          ))}

          <div style={{ borderTop: "1px solid var(--gray1)" }} />

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Text size="px14" weight="w400" color="gray5">
              Subtotal:
            </Text>
            <Text size="px14" weight="w500" color="gray9">
              {formatCurrency(subtotal)}
            </Text>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Text size="px14" weight="w400" color="gray5">
              Shipping:
            </Text>
            <Text size="px14" weight="w500" color="gray9">
              {shipping === 0 ? "Free" : formatCurrency(shipping)}
            </Text>
          </div>

          <div style={{ borderTop: "1px solid var(--gray1)" }} />

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Text size="px18" weight="w500" color="gray9">
              Total:
            </Text>
            <Text size="px18" weight="w600" color="gray9">
              {formatCurrency(total)}
            </Text>
          </div>

          <div style={{ borderTop: "1px solid var(--gray1)", paddingTop: "16px" }} />

          <Text size="px20" weight="w500" color="gray9" extraStyles={{ display: "block", textAlign: "start" }}>
            Payment Method
          </Text>

          {["Cash on Delivery", "Paypal", "Google Pay"].map((method) => (
            <label
              key={method}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="paymentMethod"
                value={method}
                checked={paymentMethod === method}
                onChange={(event) => setPaymentMethod(event.target.value)}
              />
              <Text size="px14" weight="w400" color="gray7">
                {method}
              </Text>
            </label>
          ))}

          <Button size="large" variant="fill" type="submit" style={{ width: "100%", marginTop: "8px" }}>
            Place Order
          </Button>
        </div>
      </form>
    </div>
  );
}
